import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '../../store';
import {
  Package, Clock, CheckCircle, Truck, XCircle, Search, Eye, X, Download, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const STATUS_LABEL = {
  pending: 'Beklemede', preparing: 'Hazırlanıyor', shipped: 'Kargoda',
  in_transit: 'Sevkiyatta', completed: 'Tamamlandı', cancelled: 'İptal',
};

const STATUS_COLOR = (s) => {
  if (s === 'cancelled') return 'bg-[#eef0f7] text-[#141a3d]';
  if (s === 'completed') return 'bg-green-50 text-green-700';
  return 'bg-gray-100 text-gray-700';
};

const STATUS_ICON = (s) => {
  if (s === 'cancelled') return XCircle;
  if (s === 'completed') return CheckCircle;
  if (s === 'shipped' || s === 'in_transit') return Truck;
  if (s === 'preparing') return Package;
  return Clock;
};

const PAYMENT_LABEL = (m) => {
  if (m === 'bank_transfer') return 'Havale/EFT';
  if (m === 'credit_card') return 'Kredi Kartı';
  if (m === 'on_account') return 'Cari';
  return m || '-';
};

export default function DealerOrders() {
  const { token } = useAuthStore();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(null);

  const downloadPdf = async (order, e) => {
    if (e) e.stopPropagation();
    try {
      setPdfLoading(order.id);
      const res = await axios.get(`${API_URL}/api/orders/${order.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `siparis_${order.order_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF indirildi');
    } catch { toast.error('PDF indirilemedi'); }
    finally { setPdfLoading(null); }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (filter !== 'all') params.append('status', filter);
        const res = await axios.get(`${API_URL}/api/orders?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrders(res.data.orders || []);
      } catch (err) { console.error('Error fetching orders:', err); }
      finally { setLoading(false); }
    };
    fetchOrders();
  }, [filter, token]);

  const filteredOrders = orders.filter(order => {
    if (!search) return true;
    return order.order_number.toLowerCase().includes(search.toLowerCase());
  });

  const filterTabs = [
    { value: 'all', label: 'Tümü' },
    { value: 'pending', label: 'Bekleyen' },
    { value: 'preparing', label: 'Hazırlanan' },
    { value: 'shipped', label: 'Kargoda' },
    { value: 'completed', label: 'Tamamlanan' },
    { value: 'cancelled', label: 'İptal' },
  ];

  return (
    <div className="space-y-3 sm:space-y-4" data-testid="dealer-orders">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Siparişlerim</h1>
          <p className="text-sm text-gray-500 mt-1">Tüm siparişlerinizi görüntüleyin ve takip edin</p>
        </div>
        <Link
          to="/dealer/shop"
          className="inline-flex items-center justify-center px-4 py-2 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#141a3d] transition-colors"
          data-testid="new-order-btn"
        >
          Yeni Sipariş Ver
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Sipariş numarası ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3a4680]/20 focus:border-[#5b67a0]"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {filterTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                filter === tab.value
                  ? 'bg-[#eef0f7] text-[#141a3d] border border-[#b3bbdc]'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-transparent'
              }`}
              data-testid={`filter-${tab.value}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a224f]" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <h3 className="text-base font-medium text-gray-900 mb-1">Sipariş Bulunamadı</h3>
            <p className="text-sm text-gray-500 mb-4">{filter !== 'all' ? 'Bu filtreye uygun sipariş yok.' : 'Henüz siparişiniz bulunmuyor.'}</p>
            <Link to="/dealer/shop" className="inline-flex items-center px-4 py-2 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#141a3d]">
              İlk Siparişinizi Verin
            </Link>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    <th className="w-10 px-2 py-2 text-center text-xs font-semibold text-gray-500">#</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Sipariş No</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Tarih</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Ürünler</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-20">Adet</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-32">Tutar</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-28">Ödeme</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-32">Durum</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-20">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order, i) => {
                    const StatusIcon = STATUS_ICON(order.status);
                    const kdv = order.items?.reduce((s, it) => s + (it.unit_price * it.quantity * (it.kdv_orani || 0) / 100), 0) || 0;
                    const grandTotal = order.total_amount + kdv;
                    return (
                      <tr key={order.id} className={`border-b border-gray-100 last:border-0 cursor-pointer ${i % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'} hover:bg-gray-100/50 group`} onClick={() => setSelectedOrder(order)} data-testid={`order-${order.order_number}`}>
                        <td className="px-2 py-2.5 text-center text-gray-400 text-xs">{i + 1}</td>
                        <td className="px-3 py-2.5 font-medium text-gray-900">#{order.order_number}</td>
                        <td className="px-3 py-2.5 text-gray-500 text-xs">{formatDate(order.created_at)}</td>
                        <td className="px-3 py-2.5 text-gray-600 text-xs max-w-[260px]">
                          <p className="truncate">
                            {order.items.slice(0, 2).map((it, idx) => (
                              <span key={idx}>{it.product_name}{idx < Math.min(order.items.length - 1, 1) ? ', ' : ''}</span>
                            ))}
                            {order.items.length > 2 && <span className="text-gray-400"> +{order.items.length - 2} ürün</span>}
                          </p>
                        </td>
                        <td className="px-3 py-2.5 text-center text-gray-600 tabular-nums">{order.item_count}</td>
                        <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{formatCurrency(grandTotal)}</td>
                        <td className="px-3 py-2.5 text-center">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-700">
                            {PAYMENT_LABEL(order.payment_method)}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLOR(order.status)}`}>
                            <StatusIcon className="w-3 h-3" />
                            {STATUS_LABEL[order.status] || order.status}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <div className="inline-flex items-center gap-0.5">
                            <button onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); }} className="p-1.5 text-gray-400 hover:text-[#1a224f] hover:bg-[#eef0f7] rounded transition-colors" title="Detay" data-testid={`view-order-${order.order_number}`}>
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={(e) => downloadPdf(order, e)} disabled={pdfLoading === order.id} className="p-1.5 text-gray-400 hover:text-[#1a224f] hover:bg-[#eef0f7] rounded transition-colors disabled:opacity-50" title="PDF İndir" data-testid={`pdf-${order.order_number}`}>
                              {pdfLoading === order.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filteredOrders.map((order) => {
                const StatusIcon = STATUS_ICON(order.status);
                const kdv = order.items?.reduce((s, it) => s + (it.unit_price * it.quantity * (it.kdv_orani || 0) / 100), 0) || 0;
                return (
                  <div key={order.id} className="p-3 flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors" data-testid={`order-mobile-${order.order_number}`}>
                    <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setSelectedOrder(order)}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-900">#{order.order_number}</span>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium ${STATUS_COLOR(order.status)}`}>
                          <StatusIcon className="w-2.5 h-2.5" />{STATUS_LABEL[order.status]}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-400 mt-0.5">{formatDate(order.created_at)} · {order.item_count} ürün</p>
                    </div>
                    <div className="text-right shrink-0 flex items-center gap-2">
                      <div>
                        <p className="text-sm font-bold text-gray-900">{formatCurrency(order.total_amount + kdv)}</p>
                        <span className="text-[10px] font-medium text-gray-500">{PAYMENT_LABEL(order.payment_method)}</span>
                      </div>
                      <button onClick={(e) => downloadPdf(order, e)} disabled={pdfLoading === order.id} className="p-2 text-gray-400 hover:text-[#1a224f] hover:bg-[#eef0f7] rounded transition-colors disabled:opacity-50" title="PDF İndir">
                        {pdfLoading === order.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pb-20 sm:pb-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-gray-900">#{selectedOrder.order_number}</h2>
                <p className="text-xs text-gray-500">{formatDate(selectedOrder.created_at)}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPdf(selectedOrder)} disabled={pdfLoading === selectedOrder.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1a224f] text-white rounded-lg text-xs font-medium hover:bg-[#141a3d] disabled:opacity-50 transition-colors" data-testid="modal-pdf-btn">
                  {pdfLoading === selectedOrder.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  PDF İndir
                </button>
                <button onClick={() => setSelectedOrder(null)} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="close-detail-modal"><X className="w-4 h-4 text-gray-500" /></button>
              </div>
            </div>
            <div className="p-4 space-y-5">
              {/* Status & Payment */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1.5">Sipariş Durumu</p>
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR(selectedOrder.status)}`}>
                    {React.createElement(STATUS_ICON(selectedOrder.status), { className: 'w-3.5 h-3.5' })}
                    {STATUS_LABEL[selectedOrder.status] || selectedOrder.status}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1.5">Ödeme</p>
                  <p className="text-sm font-medium text-gray-900">{PAYMENT_LABEL(selectedOrder.payment_method)}</p>
                </div>
              </div>

              {/* Items Table */}
              <div>
                <p className="text-[10px] text-gray-400 uppercase font-semibold mb-2">Sipariş İçeriği</p>
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="bg-gray-50/50 border-b border-gray-100">
                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Ürün</th>
                        <th className="text-center px-2 py-2 text-xs font-semibold text-gray-500 w-14">Adet</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-24">Birim</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-24">Toplam</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items.map((item, idx) => (
                        <tr key={idx} className={`border-b border-gray-100 last:border-0 ${idx % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'}`}>
                          <td className="px-3 py-2">
                            <p className="font-medium text-gray-900 truncate">{item.product_name}</p>
                            <p className="text-[10px] text-gray-400">
                              {item.product_barcode}
                              {item.iskonto_orani > 0 && <span className="ml-1 text-[#1a224f] font-medium">%{item.iskonto_orani} isk.</span>}
                              {item.kdv_orani > 0 && <span className="ml-1">+%{item.kdv_orani} KDV</span>}
                            </p>
                          </td>
                          <td className="px-2 py-2 text-center tabular-nums">{item.quantity}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(item.unit_price)}</td>
                          <td className="px-3 py-2 text-right font-semibold tabular-nums">{formatCurrency(item.total_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 space-y-1">
                {(() => {
                  const netTotal = selectedOrder.items?.reduce((sum, i) => sum + (i.unit_price * i.quantity), 0) || 0;
                  const kdvTotal = selectedOrder.items?.reduce((sum, i) => sum + (i.unit_price * i.quantity * (i.kdv_orani || 0) / 100), 0) || 0;
                  const grandTotal = netTotal + kdvTotal;
                  return (
                    <>
                      <div className="flex justify-between text-xs"><span className="text-gray-500">Net Tutar</span><span className="font-medium tabular-nums">{formatCurrency(netTotal)}</span></div>
                      {kdvTotal > 0 && <div className="flex justify-between text-xs"><span className="text-gray-500">KDV</span><span className="font-medium tabular-nums">{formatCurrency(kdvTotal)}</span></div>}
                      <div className="flex justify-between text-sm font-bold pt-1.5 border-t border-gray-200"><span>Genel Toplam</span><span className="tabular-nums text-[#1a224f]">{formatCurrency(grandTotal)}</span></div>
                    </>
                  );
                })()}
              </div>

              {selectedOrder.shipping_address && (
                <div>
                  <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1.5">Teslimat Adresi</p>
                  <p className="text-sm text-gray-700">{selectedOrder.shipping_address}</p>
                </div>
              )}

              {selectedOrder.shipping_note && (
                <div>
                  <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1.5">Sipariş Notu</p>
                  <p className="text-sm text-gray-700">{selectedOrder.shipping_note}</p>
                </div>
              )}

              {selectedOrder.status_history?.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-400 uppercase font-semibold mb-2">Sipariş Geçmişi</p>
                  <div className="space-y-2">
                    {selectedOrder.status_history.map((history, idx) => (
                      <div key={idx} className="flex items-start gap-2.5">
                        <div className="w-1.5 h-1.5 mt-1.5 rounded-full bg-[#1a224f] shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-gray-900">{STATUS_LABEL[history.status] || history.status}</p>
                          <p className="text-[10px] text-gray-500">
                            {formatDate(history.changed_at)}
                            {history.note && ` · ${history.note}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
